import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiFileText,
  FiCreditCard,
  FiHome,
  FiBarChart2,
  FiUsers,
  FiGrid,
  FiZap,
  FiShield,
  FiCheck,
} from "react-icons/fi";

const CATEGORIES = [
  { id: "all", label: "All Features" },
  { id: "invoicing", label: "Invoicing & Payments" },
  { id: "expenses", label: "Expense Tracking" },
  { id: "banking", label: "Banking & Reconciliation" },
  { id: "reports", label: "Financial Reports" },
  { id: "multi", label: "Multi-Company" },
  { id: "automation", label: "Automation" },
  { id: "security", label: "Security" },
];

function InvoiceCard() {
  return (
    <div className="card w-full max-w-xs p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-navy">
          Invoice #INV-1042
        </span>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-600">
          Paid
        </span>
      </div>
      <p className="mt-2 text-xl font-bold text-navy">$1,250.00</p>
      <div className="mt-3 space-y-1.5">
        <div className="h-1.5 w-full rounded bg-surface" />
        <div className="h-1.5 w-3/4 rounded bg-surface" />
        <div className="h-1.5 w-1/2 rounded bg-surface" />
      </div>
    </div>
  );
}

function ExpenseCard() {
  const rows = [
    { label: "Office Supplies", amount: "$240.00" },
    { label: "Travel", amount: "$1,120.50" },
    { label: "Software", amount: "$89.00" },
    { label: "Marketing", amount: "$460.75" },
  ];
  return (
    <div className="card w-full max-w-xs p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-navy">Expenses</span>
        <span className="text-[9px] font-medium text-slate-400">July 2026</span>
      </div>
      <div className="mt-3 space-y-2">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between text-[10px]"
          >
            <span className="text-slate-500">{r.label}</span>
            <span className="font-semibold text-navy">{r.amount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BankCard() {
  return (
    <div className="card w-full max-w-xs p-4">
      <span className="text-[11px] font-semibold text-navy">Bank</span>
      <div className="mt-2 rounded-lg border border-line p-3">
        <p className="text-[10px] text-slate-500">Checking Account</p>
        <p className="text-sm font-bold text-navy">$34,438.80</p>
      </div>
      <div className="mt-2 rounded-lg border border-line p-3">
        <p className="text-[10px] text-slate-500">Business Savings</p>
        <p className="text-sm font-bold text-navy">$91,708.25</p>
      </div>
    </div>
  );
}

function ReportCard() {
  const bars = [42, 65, 50, 80, 60, 92, 74];
  return (
    <div className="card w-full max-w-xs p-4">
      <span className="text-[11px] font-semibold text-navy">
        Revenue by Month
      </span>
      <div className="mt-3 flex h-24 items-end gap-2">
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t bg-primary/80"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function PayrollCard() {
  return (
    <div className="card w-full max-w-xs p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-navy">
          Payroll — July 2026
        </span>
      </div>
      <p className="mt-2 text-xl font-bold text-navy">$18,450.00</p>
      <p className="text-[10px] text-slate-500">8 Employees</p>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded bg-surface">
        <div className="h-full w-4/5 rounded bg-primary" />
      </div>
    </div>
  );
}

function CompaniesCard() {
  const companies = ["Acme Corporation", "Acme Real Estate LLC"];
  return (
    <div className="card w-full max-w-xs p-4">
      <span className="text-[11px] font-semibold text-navy">My Companies</span>
      <div className="mt-2 space-y-2">
        {companies.map((c) => (
          <div
            key={c}
            className="flex items-center justify-between rounded-lg border border-line px-3 py-2"
          >
            <span className="text-[10px] font-medium text-navy">{c}</span>
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold text-emerald-600">
              Active
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AutomationCard() {
  const rules = ["Recurring invoices", "Payment reminders", "Auto-categorize"];
  return (
    <div className="card w-full max-w-xs p-4">
      <span className="text-[11px] font-semibold text-navy">Automations</span>
      <div className="mt-2 space-y-2">
        {rules.map((r) => (
          <div key={r} className="flex items-center gap-2 text-[10px]">
            <FiZap className="text-primary" size={11} />
            <span className="text-slate-600">{r}</span>
            <span className="ml-auto h-3.5 w-7 rounded-full bg-primary/90 p-0.5">
              <span className="block h-2.5 w-2.5 translate-x-3.5 rounded-full bg-white" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SecurityCard() {
  return (
    <div className="card w-full max-w-xs p-4 text-center">
      <span className="mx-auto mt-1 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <FiShield size={22} />
      </span>
      <p className="mt-3 text-[11px] font-semibold text-navy">
        256-bit Encryption
      </p>
      <p className="mt-1 text-[10px] text-slate-500">
        Daily backups · 99.9% uptime · SOC 2 practices
      </p>
    </div>
  );
}

const FEATURE_ROWS = [
  {
    id: "invoicing",
    icon: FiFileText,
    title: "Invoicing & Payments",
    copy: "Create professional invoices, send payment reminders, and accept online payments.",
    bullets: [
      "Customizable invoice templates",
      "Automatic payment reminders",
      "Accept credit cards and bank transfers",
    ],
    card: <InvoiceCard />,
  },
  {
    id: "expenses",
    icon: FiCreditCard,
    title: "Expense Tracking",
    copy: "Track receipts, categorize expenses, and capture every business spend.",
    bullets: [
      "Snap and store receipts",
      "Smart expense categories",
      "Vendor and bill management",
    ],
    card: <ExpenseCard />,
  },
  {
    id: "banking",
    icon: FiHome,
    title: "Banking & Reconciliation",
    copy: "Connect your bank accounts and reconcile transactions in minutes.",
    bullets: [
      "Automatic bank feeds",
      "One-click transaction matching",
      "Multi-account support",
    ],
    card: <BankCard />,
  },
  {
    id: "reports",
    icon: FiBarChart2,
    title: "Financial Reports",
    copy: "Generate P&L, balance sheet, cash flow and more with real-time data.",
    bullets: [
      "Profit & loss, balance sheet, cash flow",
      "Real-time dashboards",
      "Export to PDF and Excel",
    ],
    card: <ReportCard />,
  },
  {
    id: "reports",
    icon: FiUsers,
    title: "Payroll & 1099",
    copy: "Run payroll, manage contractors, and generate W-2 and 1099 forms.",
    bullets: [
      "Automated payroll runs",
      "Contractor payments",
      "Year-end tax forms",
    ],
    card: <PayrollCard />,
  },
  {
    id: "multi",
    icon: FiGrid,
    title: "Multi-Company",
    copy: "Manage up to 4 companies with complete data isolation.",
    bullets: [
      "Switch companies instantly",
      "Separate books per company",
      "Consolidated overview",
    ],
    card: <CompaniesCard />,
  },
  {
    id: "automation",
    icon: FiZap,
    title: "Automation",
    copy: "Put your books on autopilot with recurring invoices, reminders, and smart rules.",
    bullets: [
      "Recurring invoices and bills",
      "Automatic payment reminders",
      "Rule-based categorization",
    ],
    card: <AutomationCard />,
  },
  {
    id: "security",
    icon: FiShield,
    title: "Security",
    copy: "Bank-level security keeps your financial data safe, always.",
    bullets: [
      "256-bit encryption at rest and in transit",
      "Two-factor authentication",
      "Daily automated backups",
    ],
    card: <SecurityCard />,
  },
];

export default function Features() {
  const [active, setActive] = useState("all");

  const rows =
    active === "all"
      ? FEATURE_ROWS
      : FEATURE_ROWS.filter((r) => r.id === active);

  return (
    <>
      <section className="section-container py-16 text-center">
        <h1 className="text-3xl font-bold sm:text-4xl">
          Powerful Features for{" "}
          <span className="text-primary">Modern Businesses</span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-slate-600">
          Money Flow Accounting provides all the tools you need to manage your finances
          efficiently and grow your business with confidence.
        </p>
      </section>

      <section className="section-container pb-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-4">
          {/* Category nav */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 flex flex-row flex-wrap gap-2 lg:flex-col">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActive(cat.id)}
                  className={`rounded-lg px-4 py-2.5 text-left text-sm font-medium transition-colors ${
                    active === cat.id
                      ? "bg-primary/10 text-primary"
                      : "text-slate-600 hover:bg-surface"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </aside>

          {/* Feature rows */}
          <div className="space-y-6 lg:col-span-3">
            {rows.map(({ icon: Icon, title, copy, bullets, card }, idx) => (
              <div
                key={`${title}-${idx}`}
                className="card flex flex-col items-start gap-6 p-6 sm:flex-row sm:items-center"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon size={18} />
                    </span>
                    <h2 className="text-lg font-semibold">{title}</h2>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{copy}</p>
                  <ul className="mt-3 space-y-1.5">
                    {bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-2 text-sm text-slate-600"
                      >
                        <FiCheck className="shrink-0 text-emerald-500" size={14} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="w-full sm:w-auto">{card}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-container pb-16">
        <div className="flex flex-col items-center justify-between gap-6 rounded-2xl bg-navy px-6 py-10 text-center sm:flex-row sm:px-12 sm:text-left">
          <div>
            <h2 className="text-xl font-bold text-white sm:text-2xl">
              All the features you need. One simple platform.
            </h2>
            <p className="mt-2 text-sm text-slate-300">
              Get started today and experience the difference.
            </p>
          </div>
          <Link to="/register" className="btn-primary shrink-0">
            Get Started
          </Link>
        </div>
      </section>
    </>
  );
}
